import { BufferGeometry } from "three";
import { Renderer } from "./Renderer";
import { USE_OUT_OF_CORE_TILE_MANAGER, DISABLE_STREAMING_DRAW } from "../globals";
import { UsingMultiDraw, UsingDrawIndirect } from "./globals";
import { BYTES_PER_DRAWCALL } from "./gpudrivenrender/IndirectBufferBuilder";
import { BufferManagerWebGPU } from './BufferManagerWebGPU';
import { Megabytes } from "../globals";
import { MAX_BYTES_PER_SECOND, MAPPED_BUF_SIZE } from "../globals";
/** @import { MultiDrawBatch } from "./gpudrivenrender/MultiDrawBatch" */


/** @enum {number} IndirectDrawType */
const IndirectDrawType = {
  NONE: 0,
  INDIRECT: 1,
  MULTI_DRAW: 2
};

export class VertexBuffer {

  /** @type {GPUBuffer|undefined} */
  #curVB;
  /** @type {GPUBuffer|undefined} */
  #curIB;
  /** @type {"uint16" | "uint32"} */
  #curIBType;
  /** @type {StateTrackingEncoder|undefined} */
  #curPass;

  /** @type {GPUDevice} */
  #device;

  /** @type {BufferManagerWebGPU} */
  #uploadIndexBufferManager;
  /** @type {BufferManagerWebGPU} */
  #streamIndexBufferManager;
  /** @type {BufferManagerWebGPU} */
  #uploadVertexBufferManager;
  /** @type {BufferManagerWebGPU} */
  #streamVertexBufferManager;

  /** @type {StagingBuffers} */
  #staging;
  /** @type {Set.<BufferGeometry>} */
  #streamDraws = new Set();
  #cleanups = 0;
  #bytesWritten = 0;
  #overWriteLimit = false;
  #dataFlushes = 0;
  #numWrites = 0;
  #numDraws = 0;
  /** @type {number} */
  #maxBytesPerFrame; // set based on fps target

  /**
   * @type {boolean} Whether progressive rendering is enabled in the Viewer:
   *                   - If true, we limit the upload budget per frame to support progressive rendering.
   *                   - If false, we disable the limit to force full frames.
   **/
  #progressive = true;

  /** @type {IndirectDrawType} */
  #indirectDrawType = IndirectDrawType.NONE;

  /**
   * @param {Renderer} renderer
   */
  constructor(renderer) {
    const device = renderer.getDevice();
    this.#device = device;

    const vertexBufferDesc = {
      label: 'vertex-buffer',
      size: Megabytes(64),
      usage: GPUBufferUsage.VERTEX | GPUBufferUsage.COPY_DST
    };

    const indexBufferDesc = {
      label: 'index-buffer',
      size: Megabytes(16),
      usage: GPUBufferUsage.INDEX | GPUBufferUsage.COPY_DST
    };

    this.#uploadIndexBufferManager = new BufferManagerWebGPU(device, indexBufferDesc, { freesAreBatched: true });
    this.#uploadVertexBufferManager = new BufferManagerWebGPU(device, vertexBufferDesc, { freesAreBatched: true });

    this.#streamIndexBufferManager = new BufferManagerWebGPU(device, indexBufferDesc, { freesAreBatched: true });
    this.#streamVertexBufferManager = new BufferManagerWebGPU(device, vertexBufferDesc, { freesAreBatched: true });

    this.#staging = new StagingBuffers(device);
    this.setFpsTarget(30);

    if (UsingMultiDraw()) {
      this.#indirectDrawType = IndirectDrawType.MULTI_DRAW;
    } else if (UsingDrawIndirect()) {
      this.#indirectDrawType = IndirectDrawType.INDIRECT;
    }
  }

  static #disposeHandler(event) {
    const geometry = event.target;
    geometry.removeEventListener('dispose', VertexBuffer.#disposeHandler);
    VertexBuffer.deallocateGeometry(geometry);
  }

  /**
   * Free up vertex buffer allocatations for this geometry, if there are any.
   * @param {BufferGeometry} geometry
  */
  static deallocateGeometry(geometry) {
    // The event listener class overrides the this context when invoking this handler.
    // So we need to access the BufferManagers instance via geometry.__gpuVertexBufferManager and geometry.__gpuIndexBufferManager.
    if (geometry.__gpuvb) {
      const block = geometry.__gpuvbBlock;
      const bufferManager = geometry.__gpuVertexBufferManager;
      if (block) {
        bufferManager.free(block);
      }
      geometry.__gpuvb = undefined;
      geometry.__gpuvbBlock = undefined;
      geometry.__gpuvbSize = undefined;
      geometry.__gpuvbBaseVertex = undefined;
    }

    if (geometry.__gpuib) {
      const block = geometry.__gpuibBlock;
      const bufferManager = geometry.__gpuIndexBufferManager;
      if (block) {
        bufferManager.free(block);
      }
      geometry.__gpuib = undefined;
      geometry.__gpuibBlock = undefined;
      geometry.__gpuibSize = undefined;
      geometry.__gpuibBaseIndex = undefined;
      geometry.__gpuibLinesOffset = undefined;
      geometry.__gpuibType = undefined;
      geometry.__gpuibShift = undefined;
    }

    geometry.__gpuVertexBufferManager = undefined;
    geometry.__gpuIndexBufferManager = undefined;
  }

  /**
   * Flush all buffer copies from the staging buffers into the upload and stream
   * allocator buffers.
   * flushWrites() needs to be called before submitting any command buffers that
   * referenced any of VertexBuffer's GPUBuffers.
   */
  flushWrites() {
    if (this.#staging.flushData()) {
      this.#dataFlushes++;
    }
  }

  /**
   * @param {number} fpsTarget
   */
  setFpsTarget(fpsTarget) {
    this.maxBytesPerFrame = MAX_BYTES_PER_SECOND / fpsTarget; // default to 30fps
    this.#staging.maxBufsPerFrame = Math.ceil(this.maxBytesPerFrame / MAPPED_BUF_SIZE);
  }

  /**
   * When progressive rendering is disabled the per-frame upload budget must not be
   * enforced: a full render must be able to upload all geometry in a single frame.
   * @param {boolean} progressive
   */
  setProgressiveRendering(progressive) {
    this.#progressive = progressive;
  }

  /**
   * @param {GPUBuffer} dstBuffer
   * @param {number} dstOffset
   * @param {ArrayBuffer} srcBuffer
   * @param {number} srcOffset
   * @param {number} size
   */
  #writeBuffer(dstBuffer, dstOffset, srcBuffer, srcOffset, size) {
    this.#bytesWritten += size;
    this.#numWrites++;
    if (this.#bytesWritten > this.maxBytesPerFrame) {
      this.#overWriteLimit = true;
    }

    // If the staging buffers are full, fallback to GPUQueue.writeBuffer().
    if (!this.#staging.writeData(dstBuffer, dstOffset, srcBuffer, srcOffset, size)) {
      this.#device.queue.writeBuffer(dstBuffer, dstOffset, srcBuffer, srcOffset, size);
    }
  }

  /**
   *
   * @param {BufferGeometry} geometry
   * @param {boolean} useStreamAllocator
   */
  #initVB(geometry, useStreamAllocator) {
    if ((geometry.__gpuvb || geometry.__gpuib) && !geometry.vbNeedsUpdate) {
      return;
    }

    // If we've initialized this VertexBuffer already we free up the GPU memory
    // we used previously.
    // @todo: We could instead detect if the previously used buffers are the same size
    // as what we want this time, and reuse them if so. This would save a small amount
    // processing time, and potentially reduce some fragmentation of the buffer.
    if (geometry.__gpuvb || geometry.__gpuib) {
      VertexBuffer.deallocateGeometry(geometry);
    }

    const vertexBufferManager = useStreamAllocator ? this.#streamVertexBufferManager : this.#uploadVertexBufferManager;
    const indexBufferManager = useStreamAllocator ? this.#streamIndexBufferManager : this.#uploadIndexBufferManager;

    geometry.__gpuVertexBufferManager = vertexBufferManager;
    geometry.__gpuIndexBufferManager = indexBufferManager;

    let strideBytes = geometry.vbstride * 4;
    const memoryBlock = vertexBufferManager.allocate(geometry.vb.byteLength, strideBytes);

    const vb = memoryBlock.buffer;
    const baseVertex = memoryBlock.offset / strideBytes;
    this.#writeBuffer(vb, baseVertex * strideBytes, geometry.vb.buffer, geometry.vb.byteOffset, geometry.vb.byteLength);

    geometry.__gpuvb = vb;
    geometry.__gpuvbBlock = memoryBlock;
    geometry.__gpuvbSize = geometry.vb.byteLength;
    geometry.__gpuvbBaseVertex = baseVertex;

    let len4 = geometry.ib.byteLength;
    if (len4 % 4) len4 += 2;

    let lenLines = geometry.iblines?.byteLength || 0;
    if (lenLines % 4) lenLines += 2;

    const ibSize = len4 + lenLines;
    const indexAlloc = indexBufferManager.allocate(ibSize);
    const ib = indexAlloc.buffer;
    const baseIndexOffset = indexAlloc.offset;

    if (geometry.ib.byteOffset + len4 > geometry.ib.buffer.byteLength) {
      // TODO: https://jira.autodesk.com/browse/VIZX-1666
      // This log is very noisy.
      // console.log("OUT OF BOUNDS FIX", ib.offset, len4);
      //This is an annoying case where the underlying array buffer doesn't have extra bytes to
      //compensate for the extra 2 bytes we add to the copy length to make it multiple of 4
      //Note that only triangle index buffers can be non-multiple of 4.
      let tmp = new Uint16Array(new ArrayBuffer(len4));
      tmp.set(geometry.ib);
      this.#writeBuffer(ib, baseIndexOffset, tmp.buffer, 0, len4);
    } else {
      this.#writeBuffer(ib, baseIndexOffset, geometry.ib.buffer, geometry.ib.byteOffset, len4);
    }

    if (geometry.iblines) {
      this.#writeBuffer(ib, baseIndexOffset + len4,
      geometry.iblines.buffer, geometry.iblines.byteOffset, lenLines);
    }

    geometry.__gpuib = ib;
    geometry.__gpuibBlock = indexAlloc;

    if (geometry.ib instanceof Uint16Array) {
      geometry.__gpuibType = "uint16";
      geometry.__gpuibShift = 1;
      geometry.iblines && (geometry.__gpuibLinesOffset = len4 / 2);
    } else if (geometry.ib instanceof Uint32Array) {
      geometry.__gpuibType = "uint32";
      geometry.__gpuibShift = 2;
      geometry.iblines && (geometry.__gpuibLinesOffset = len4 / 4);
    } else {
      console.warn("unknown index buffer type");
    }

    geometry.__gpuibBaseIndex = baseIndexOffset >> geometry.__gpuibShift;
    geometry.__gpuibSize = ibSize;

    geometry.vbNeedsUpdate = false;

    if (vertexBufferManager === this.#uploadVertexBufferManager) {
      geometry.addEventListener('dispose', VertexBuffer.#disposeHandler);
    }

    // This is useful to debug memory leaks.
    // console.log(geometry.id, vertexBufferManager.computeUsedBytes());
  }

  /**
   * @param {BufferGeometry} geom
  */
  initUploaded(geom) {
    if (this.#streamDraws.has(geom)) {
      VertexBuffer.deallocateGeometry(geom);
      this.#streamDraws.delete(geom);
    }

    this.#initVB(geom, false);
  }

  /**
   * @param {BufferGeometry} geom
  */
  initStream(geom) {
    if (this.#streamDraws.has(geom)) {
      return;
    }

    this.#initVB(geom, true);
    this.#streamDraws.add(geom);
  }

  /**
   * Free up all blocks in the stream allocator.
   */
  #clearStreamBuffers() {
    for (const geom of this.#streamDraws) {
      if (geom.__gpuVertexBufferManager === this.#streamVertexBufferManager) {
        VertexBuffer.deallocateGeometry(geom);
      }
    }
    this.#streamDraws.clear();
    this.#streamVertexBufferManager.flushFreeBlocks();
    this.#streamIndexBufferManager.flushFreeBlocks();
  }

  /**
   * Free up unneeded memory and per-frame state tracking.
   * Should be called at the end of each frame.
   */
  cleanup() {
    this.#uploadIndexBufferManager.flushFreeBlocks();
    this.#uploadVertexBufferManager.flushFreeBlocks();
    this.#clearStreamBuffers();
    this.#staging.cleanup();

    this.#bytesWritten = 0;
    this.#overWriteLimit = false;
    this.#cleanups++;
    this.#dataFlushes = 0;
    this.#numWrites = 0;
    this.#numDraws = 0;
  }

  stats() {
    const totalMem = this.getTotalMemory() / 1024 / 1024 / 1024; // GB
    console.log(`Vertex Buffer Memory: ${totalMem} GB`);
    console.log(`Bytes written: ${this.#bytesWritten / 1024 / 1024} MB`);
    console.log(`Writes: ${this.#numWrites}`);
    console.log(`Flushed times: ${this.#dataFlushes}`);
    console.log(`Draws: ${this.#numDraws}`);
  }

  /**
   * Get the total amount of memory being used, not counting the overhead of unused
   * space in currently allocated buffers.
   * @returns {number}
   */
  getTotalMemory() {
    let total = 0;
    total += this.#uploadVertexBufferManager.getTotalSize();
    total += this.#uploadIndexBufferManager.getTotalSize();
    total += this.#streamVertexBufferManager.getTotalSize();
    total += this.#streamIndexBufferManager.getTotalSize();
    total += this.#staging.totalAllocated();
    return total;
  }

  /**
   * @param {Buffer} geometry
   * @returns {boolean}
   */
  canDraw(geometry) {
    if (!USE_OUT_OF_CORE_TILE_MANAGER) {
      return true;
    }

    const needsUpload = !geometry.__gpuvb || geometry.vbNeedsUpdate;
    if (!needsUpload) {
      return true;
    } else {
      const needed = geometry.vb.byteLength + geometry.ib.byteLength + (
      geometry.iblines?.byteLength || 0) + 4;
      return this.canUpload(needed);
    }
  }

  /**
   * @param {number} size
   * @returns {boolean} Whether or not size bytes can be uploaded this frame.
   */
  canUpload(size) {
    if (!this.#progressive) {
      return true;
    }

    // Allow one geometry per frame to be uploaded if it's bigger than the max
    // allowed copied bytes per frame.
    if (size > this.maxBytesPerFrame && !this.#overWriteLimit) {
      return true;
    }
    const leftThisFrame = this.maxBytesPerFrame - this.#bytesWritten;
    return leftThisFrame > size;
  }

  /**
   * Make sure that vb and ib are set on the passEncoder
   * @param {StateTrackingEncoder} passEncoder
   * @param {GPUBuffer} vb
   * @param {GPUBuffer} ib
   * @param {"uint16" | "uint32"} ibType
   */
  #setCurrentBuffers(passEncoder, vb, ib, ibType) {

    // If passEncoder changed, set both buffers
    if (this.#curPass !== passEncoder) {
      passEncoder.setVertexBuffer(0, vb);
      this.#curVB = vb;

      passEncoder.setIndexBuffer(ib, ibType);
      this.#curIB = ib;
      this.#curIBType = ibType;

      this.#curPass = passEncoder;
    } else {
      // passEncoder is the same - only set the buffers if they changed
      if (vb !== this.#curVB) {
        passEncoder.setVertexBuffer(0, vb);
        this.#curVB = vb;
      }

      if (ib !== this.#curIB || ibType !== this.#curIBType) {
        passEncoder.setIndexBuffer(ib, ibType);
        this.#curIB = ib;
        this.#curIBType = ibType;
      }
    }
  }

  /**
   * @param {StateTrackingEncoder} passEncoder
   * @param {BufferGeometry} geometry
   */
  #prepareDraw(passEncoder, geometry) {
    const geometryNeedsUpdate = !geometry.__gpuvb || geometry.vbNeedsUpdate;

    if (USE_OUT_OF_CORE_TILE_MANAGER && geometryNeedsUpdate) {
      if (geometry.streamingDraw && !DISABLE_STREAMING_DRAW) {
        // Currently happening for unoptimized 3D model tiles and overlays.
        // For Tandem, we never use this.
        this.initStream(geometry);
      } else {
        // Currently happening for 2D geometry and tiles selected for optimization by OutOfCoreTileManager.
        // Done for all geometry on Tandem (DISABLE_STREAMING_DRAW).
        this.initUploaded(geometry);
      }
    } else if (geometryNeedsUpdate) {
      // All geometry is uploaded at draw time when not using OutOfCoreTileManager.
      this.initUploaded(geometry);
    }

    const vb = geometry.__gpuvb;
    const ib = geometry.__gpuib;
    if (!vb || !ib) {
      console.error('Still missing VB or IB when drawing!');
      return;
    }

    this.#numDraws++;
    this.#setCurrentBuffers(passEncoder, vb, ib, geometry.__gpuibType);
  }

  /**
   * @param {StateTrackingEncoder} passEncoder
   * @param {BufferGeometry} geometry
   * @param {number} instanceId
   */
  draw(passEncoder, geometry, instanceId) {
    this.#prepareDraw(passEncoder, geometry);

    const instanceCount = geometry.numInstances ?? 1;
    passEncoder.drawIndexed(geometry.ib.length, instanceCount, geometry.__gpuibBaseIndex, geometry.__gpuvbBaseVertex, instanceId);
  }

  /**
   * @param {StateTrackingEncoder} passEncoder
   * @param {BufferGeometry} geometry
   * @param {number} instanceId
   */
  drawEdges(passEncoder, geometry, instanceId) {
    if (!geometry.iblines) {
      return;
    }

    this.#prepareDraw(passEncoder, geometry);

    const instanceCount = geometry.numInstances ?? 1;
    let firstIndex = geometry.__gpuibBaseIndex + geometry.__gpuibLinesOffset;
    passEncoder.drawIndexed(geometry.iblines.length, instanceCount, firstIndex, geometry.__gpuvbBaseVertex, instanceId);
  }

  /** Run a multi-draw call for the given geometry.
   * Note: It's required that vb and ib are already uploaded.
   * @param {StateTrackingEncoder} passEncoder
   * @param {MultiDrawCall} multiDraw
   * @param {MultiDrawBatch} mdBatch
   * @param {bool} [drawEdges=false] By Default, we render geometry. If true, we render edges instead.
   **/
  drawMulti(passEncoder, multiDraw, mdBatch, drawEdges = false) {
    if (this.#indirectDrawType === IndirectDrawType.NONE) {
      console.error('VertexBuffer.drawMulti() was called but indirect draws are disabled.');
      return;
    }

    // Make sure vertex buffer and index buffer are set
    const vb = multiDraw.vb;
    const ib = multiDraw.ib;
    const ibType = multiDraw.firstGeometry.__gpuibType;
    this.#setCurrentBuffers(passEncoder, vb, ib, ibType);

    let drawCallsBlock = drawEdges ?
    mdBatch.edgeDrawCallsBlock :
    mdBatch.drawCallsBlock;

    if (this.#indirectDrawType === IndirectDrawType.MULTI_DRAW) {
      passEncoder.multiDrawIndexedIndirect(
        drawCallsBlock.buffer,
        drawCallsBlock.offset + multiDraw.indirectOffset,
        multiDraw.drawCount,
        mdBatch.drawCallCountsBuffer
      );

    } else if (this.#indirectDrawType === IndirectDrawType.INDIRECT) {
      for (let i = 0; i < multiDraw.drawCount; i++) {
        passEncoder.drawIndexedIndirect(
          drawCallsBlock.buffer,
          drawCallsBlock.offset + multiDraw.indirectOffset + BYTES_PER_DRAWCALL * i);
      }
    }
  }
}

class StagingBuffers {
  /** @type {GPUDevice} */
  #device;

  /**
   * @typedef {Object} MappedBuffer
   * @property {GPUBuffer} buffer
   * @property {ArrayBuffer|null} range
   * @property {number} offset
   * @property {number} size
   */

  /** @type {MappedBuffer[]} */
  #mappedBuffers = [];
  /** @type {GPUCommandEncoder} */
  #copyEncoder;
  /** @type {MappedBuffer[]} */
  #usedThisFrame = [];

  /** @type {number} */
  maxBufsPerFrame = 10;

  /**
   * @param {GPUDevice} device
   */
  constructor(device) {
    this.#device = device;
  }

  /**
   * @returns {MappedBuffer}
   */
  #getMappedBuffer() {
    if (this.#mappedBuffers.length) {
      return this.#mappedBuffers.shift();
    }

    const buffer = this.#device.createBuffer({
      label: 'staging-buffer',
      size: MAPPED_BUF_SIZE,
      usage: GPUBufferUsage.MAP_WRITE | GPUBufferUsage.COPY_SRC,
      mappedAtCreation: true
    });
    return {
      buffer,
      range: null,
      offset: 0,
      size: MAPPED_BUF_SIZE
    };
  }

  /**
   * @param {number} size
   * @returns {MappedBuffer|null}
   */
  #getBufferWithSpace(size) {
    for (const mapped of this.#usedThisFrame) {
      if (mapped.buffer.mapState != 'mapped') {
        continue;
      }
      if (mapped.size - mapped.offset > size) {
        return mapped;
      }
    }

    if (this.#usedThisFrame.length >= this.maxBufsPerFrame) {
      return null;
    }

    const newThisFrame = this.#getMappedBuffer();
    // Add to the beginning of the list so the above loop finds it first.
    this.#usedThisFrame.unshift(newThisFrame);
    return newThisFrame;
  }

  totalAllocated() {
    let total = 0;
    for (const mapped of this.#mappedBuffers) {
      total += mapped.size;
    }
    for (const mapped of this.#usedThisFrame) {
      total += mapped.size;
    }
    return total;
  }

  /**
   * @param {GPUBuffer} dstBuffer
   * @param {number} dstOffset
   * @param {ArrayBuffer} srcBuffer
   * @param {number} srcOffset
   * @param {number} size
   * @returns {boolean} Whether or not the write was successful.
   *   Writes could fail if there is not suffient space in any staging buffers
   *   left this frame, or if size is bigger than the staging buffer size.
   */
  writeData(dstBuffer, dstOffset, srcBuffer, srcOffset, size) {
    if (size > MAPPED_BUF_SIZE) {
      return false;
    }

    const mapped = this.#getBufferWithSpace(size);
    if (!mapped) {
      return false;
    }

    if (!mapped.range) {
      mapped.range = mapped.buffer.getMappedRange();
    }

    const mappedArray = new Uint8Array(mapped.range);
    mappedArray.set(new Uint8Array(srcBuffer, srcOffset, size), mapped.offset);

    if (!this.#copyEncoder) {
      this.#copyEncoder = this.#device.createCommandEncoder();
    }
    this.#copyEncoder.copyBufferToBuffer(
      mapped.buffer, mapped.offset, dstBuffer, dstOffset, size);

    mapped.offset += size;

    return true;
  }

  /**
   * Submit copies that have been written to staging buffers since
   * the last flushData() call.
   * @returns {boolean} Whether or not any copies were flushed.
   */
  flushData() {
    if (!this.#copyEncoder) {
      return false;
    }

    for (const mapped of this.#usedThisFrame) {
      if (mapped.buffer.mapState == 'mapped') {
        mapped.buffer.unmap();
        mapped.range = null;
      }
    }

    this.#device.queue.submit([this.#copyEncoder.finish()]);
    this.#copyEncoder = null;

    return true;
  }

  cleanup() {
    // In cases where geometry was uploaded this frame but not yet drawn,
    // there may be some copies to flush.
    this.flushData();

    for (const unmapped of this.#usedThisFrame) {
      if (unmapped.buffer.mapState == 'unmapped') {
        unmapped.buffer.mapAsync(GPUMapMode.WRITE).
        then(() => {
          this.#mappedBuffers.push({
            ...unmapped,
            offset: 0
          });
        });
      }
    }

    this.#usedThisFrame = [];
  }
}